/**
 * @file unsubscribe.js
 * @description Client side of the cold-outreach unsubscribe link.
 *
 *   Every cold-outreach email points its unsubscribe link at
 *   `/unsubscribe?token=<uuid>` on abacases.com — NOT at the edge function
 *   directly, for the same two reasons documented in interviewRespond.js: the
 *   edge-function gateway rewrites Content-Type to text/plain so a function
 *   cannot serve a page, and a `<ref>.supabase.co` link in an inbox reads as
 *   phishing.
 *
 *   GET previews (confirms the token resolves, shows the masked email, zero
 *   writes); POST performs the actual unsubscribe. This page is the only
 *   caller of POST — see cold-outreach-unsubscribe's file header for why GET
 *   must never write.
 *
 *   Deliberately does NOT use the shared supabase client: must work logged
 *   out, must not attach the reader's session (mail gets forwarded, and
 *   `info@` addresses are shared inboxes), and the function runs with
 *   verify_jwt = false so no key is needed.
 *
 * @module lib/unsubscribe
 */
import { supabaseUrl } from "./supabase";

/** Public route the email links point at. Intentionally NOT in seoRoutes.json. */
export const UNSUBSCRIBE_PATH = "/unsubscribe";

const FN_URL = `${supabaseUrl}/functions/v1/cold-outreach-unsubscribe`;

/** True when the given pathname is the unsubscribe route (trailing slash tolerated). */
export function isUnsubscribePath(pathname) {
  return String(pathname || "").replace(/\/+$/, "") === UNSUBSCRIBE_PATH;
}

/** Pull the token out of a query string, e.g. window.location.search. */
export function readUnsubscribeToken(search) {
  return new URLSearchParams(search || "").get("token") || "";
}

async function call(method, token) {
  try {
    const url = method === "GET" ? `${FN_URL}?token=${encodeURIComponent(token)}` : FN_URL;
    const res = await fetch(url, {
      method,
      headers: method === "POST" ? { "Content-Type": "application/json" } : undefined,
      body: method === "POST" ? JSON.stringify({ token }) : undefined,
    });
    const body = await res.json().catch(() => ({}));
    return { ...body, httpStatus: res.status };
  } catch {
    return { ok: false, reason: "network_error", httpStatus: 0 };
  }
}

/** GET — preview only, zero writes. */
export function previewUnsubscribe(token) {
  return call("GET", token);
}

/** POST — performs the actual unsubscribe. */
export function confirmUnsubscribe(token) {
  return call("POST", token);
}
